import type { Fraction } from '../util/fractionType';
import type { OutputProfile } from './outputProfile';
import { getFieldOrderFromProfile, resolveFormat, resolveImageFormat } from './outputProfile';
import type { FieldOrder, Resolution } from './types';
import { safeArrayAccess } from '../util/safeArrayAccess';

export type FilterableOutputProfile = OutputProfile & ({
  type: 'generic',
} | {
  type: 'broadcast',
  brandName: string,
  variant: string | undefined,
});

export function isFilterableOutputProfile(value: OutputProfile): value is FilterableOutputProfile {
  if (!('type' in value)) {
    return false;
  }
  const profile = value as OutputProfile & { type: unknown };
  return typeof profile.type === 'string' && profile.type === 'broadcast' || profile.type === 'generic';
}

export interface ProfileFilterDefinition {
  label: string,
  profileToChoice: (profile: FilterableOutputProfile) => string,
}

export const BRAND_NAME_FILTER : ProfileFilterDefinition = {
  label: 'Brand',
  profileToChoice: (profile) => { return profile.type === 'broadcast' ?  profile.brandName : '(none)'; },
};

export const VARIANT_FILTER : ProfileFilterDefinition = {
  label: 'Variant',
  profileToChoice: (profile) => { return profile.type === 'broadcast' ? profile.variant ?? '(none)' : '(none'; },
};

export const RESOLUTION_FILTER : ProfileFilterDefinition = {
  label: 'Resolution',
  profileToChoice: (profile) => {
    let text = '(none)';
    const value : Resolution | undefined = profile.video?.resolution;
    if (value !== undefined) {
      text = `${value.width}x${value.height}`;
    }
    return text;
  },
};

export const FIELD_ORDER_CHOICE : ProfileFilterDefinition = {
  label: 'Field order',
  profileToChoice: (profile) => {
    let text;
    const value : FieldOrder | undefined = getFieldOrderFromProfile(profile);
    if (value === undefined) {
      text = '(none)';
    } else if (value === 'progressive') {
      text = 'Progressive';
    } else if (value === 'top_field_first') {
      text = 'Interlaced (Top field first)';
    } else {
      text = 'Interlaced (Bottom field first)';
    }
    return text;
  }
};

export const FRAME_RATE_CHOICE : ProfileFilterDefinition = {
  label: 'Frame rate',
  profileToChoice: (profile) => {
    let text = '(none)';
    const value : Fraction | undefined = profile.video?.frameRate;
    if (value !== undefined) {
      const floored = Math.floor(value.numerator / value.denominator);
      text = `${floored}`;
    }
    return text;
  },
};

export const AUDIO_CHANNEL_COUNT_CHOICE : ProfileFilterDefinition = {
  label: 'Audio channels',
  profileToChoice: (profile) => {
    if (profile.audio === undefined) {
      return '(no audio)';
    } else {
      const channelLimits = profile.audio.channelLimits;
      if ((channelLimits?.max === undefined) || (channelLimits?.max !== channelLimits?.min)) {
        return '(same as source)';
      }
      return `${channelLimits.max}`;
    }
  },
};

export const VIDEO_MODE_CHOICE : ProfileFilterDefinition = {
  label: 'Mode',
  profileToChoice: (profile) => {
    const video = profile.video;
    if (video === undefined) {
      return '(no video)';
    }
    const fieldOrder = getFieldOrderFromProfile(profile);
    const isProgressive = fieldOrder === 'progressive';
    let anamorphic : string = '';
    let resolution : string = '';
    let orientation: string = '';
    if (video.resolution !== undefined) {
      let height: number = video.resolution.height;
      let width: number = video.resolution.width;

      if (height === width) {
        orientation = ' (Square)';
      } else if (width < height) {
        orientation = ' (Portrait)';
        // swapping height and width
        [height, width] = [width, height];
      }

      if (height <= 1920) {
        const scanModeIndicator = isProgressive ? 'p' : 'i';
        resolution = `${height}${scanModeIndicator}`;
        if (width === 1440) {
          anamorphic = ' (anamorphic)';
        }
      } else if (width == 3840) {
        resolution = 'UHD';
      } else if (width == 4096) {
        resolution = '4K';
      } else {
        resolution = '<unknown>';
      }
    }

    let fieldRate : string = '';
    if (video.frameRate !== undefined) {
      const fieldsPerFrame = isProgressive ? 1 : 2;
      const framesPerSecond = video.frameRate.numerator / video.frameRate.denominator;
      fieldRate = `${Math.floor(framesPerSecond * fieldsPerFrame)}`;
    }

    return `${resolution}${fieldRate}${anamorphic}${orientation}` || '(same as source)';
  },
};

const CONTAINER_FORMAT_FILTER : ProfileFilterDefinition = {
  label: 'Container format',
  profileToChoice: (profile) => {
    if (profile.container.type === 'rtmp') {
      return 'flv';
    }
    if (profile.container.type === 'images') {
      return resolveImageFormat(profile.container.format).name;
    } else {
      return resolveFormat(profile.container.format).name;
    }
  },
};

const VIDEO_CODEC_FILTER : ProfileFilterDefinition = {
  label: 'Video codec',
  profileToChoice: (profile) => {
    return profile.video?.codec.codec ?? '(none)';
  },
};

const AUDIO_CODEC_FILTER : ProfileFilterDefinition = {
  label: 'Audio codec',
  profileToChoice: (profile) => {
    return profile.audio?.codec.codec ?? '(none)';
  },
};

export const COLOR_SPACE_FILTER : ProfileFilterDefinition = {
  label: 'Color space',
  profileToChoice: (profile) => {
    const colorSpace = profile.video?.colorSpace;
    if (!colorSpace) {
      return 'Same as source';
    }

    const components : string[] = [];

    const matrixType = colorSpace.matrix.type;
    components.push(matrixType);

    if (colorSpace.primaries !== matrixType) {
      components.push(colorSpace.primaries);
    }
    if (colorSpace.transfer !== matrixType) {
      components.push(colorSpace.transfer);
    }
    return components.join('/');
  }
};


export const COMPACT_BROADCAST_FILTER_DEFINITIONS: ProfileFilterDefinition[] = [
  BRAND_NAME_FILTER,
  VIDEO_MODE_CHOICE,
  VARIANT_FILTER,
  AUDIO_CHANNEL_COUNT_CHOICE,
  COLOR_SPACE_FILTER,
];

export const COMPACT_GENERIC_FILTER_DEFINITIONS: ProfileFilterDefinition[] = [
  CONTAINER_FORMAT_FILTER,
  VIDEO_CODEC_FILTER,
  AUDIO_CODEC_FILTER,
  VIDEO_MODE_CHOICE,
  AUDIO_CHANNEL_COUNT_CHOICE,
];

export interface ProfileFilterContext {
  label: string,
  makeChoice: (profile: FilterableOutputProfile) => string,
  choices: string[],
  selected: string,
}

export interface ProfileFilterChain {
  filters: ProfileFilterContext[],
  update: (profiles: FilterableOutputProfile[]) => FilterableOutputProfile[],
  setProfile: (profile: FilterableOutputProfile) => void,
}

export function createProfileFilterChain(
  filterDefinitions: ProfileFilterDefinition[],
): ProfileFilterChain {
  const filters : ProfileFilterContext[] = [];
  for (const profileFilterDefinition of filterDefinitions) {
    const profileToChoice = profileFilterDefinition.profileToChoice;
    const makeChoice = (profile: FilterableOutputProfile): string => {
      return profileToChoice(profile);
    };
    const profileFilterContext = {
      label: `${profileFilterDefinition.label}`,
      makeChoice,
      choices: [],
      selected: '',
    };
    filters.push(profileFilterContext);
  }
  return {
    filters,
    update: (profiles: FilterableOutputProfile[]) => {
      return updateProfileFilterChain(filters, profiles);
    },
    setProfile: (profile: FilterableOutputProfile) => {
      for (const filter of filters) {
        filter.selected = filter.makeChoice(profile);
      }
    },
  };
}

function updateProfileFilterChain(
  filters: ProfileFilterContext[],
  filteredProfiles: FilterableOutputProfile[],
): FilterableOutputProfile[] {
  for (const filter of filters) {
    const choiceSet = new Set<string>();
    for (const profile of filteredProfiles) {
      choiceSet.add(filter.makeChoice(profile));
    }
    filter.choices = [...choiceSet];

    const foundChoice = filter.choices.find((choice) => { return filter.selected === choice; });
    if (foundChoice === undefined) {
      // Selected choice is no longer available
      filter.selected = safeArrayAccess(filter.choices, 0);
    }

    filteredProfiles = filteredProfiles.filter((profile) => {
      return filter.makeChoice(profile) === filter.selected;
    });
  }
  return filteredProfiles;
}

export type FilterChoice = {
  label: string,
  value: string,
};

export function filterProfiles(
  filters: ProfileFilterDefinition[],
  profiles: FilterableOutputProfile[],
  choices: FilterChoice[],
): FilterableOutputProfile[] {
  for (const choice of choices) {
    const filterDef = filters.find((def) => { return def.label === choice.label; });
    if (!filterDef) {
      continue;
    }
    profiles = profiles.filter((profile) => {
      return filterDef.profileToChoice(profile) === choice.value;
    });
  }
  return profiles;
}
